import { useAuth } from "../context/AuthContext";
import type { UserRole, Permission } from "../types/auth";
import { ROLE_CONFIG } from "../config/roles";

export const usePermissions = () => {
    const { user, hasPermission, hasRole, canAccessRoute: checkRouteAccess } = useAuth();

    const isAdmin = (): boolean => {
        return hasRole(['admin', 'superadmin']);
    };

    const isSuperAdmin = (): boolean => {
        return hasRole('superadmin');
    };

    const isManager = (): boolean => {
        return hasRole(['user', 'admin', 'superadmin']);
    };

    const isAgent = (): boolean => {
        return hasRole(['user', 'admin', 'superadmin']);
    };

    const canAccess = (requiredRoles?: UserRole[], requiredPermissions?: Permission[]): boolean => {
        if (requiredRoles && !hasRole(requiredRoles)) {
            return false;
        }

        if (requiredPermissions) {
            return requiredPermissions.every(permission => hasPermission(permission));
        }

        return true;
    };

    const canAccessRoute = (routePath: string): boolean => {
        return checkRouteAccess(routePath);
    };

    const canPerformAction = (action: Permission): boolean => {
        return hasPermission(action);
    };

    const getUserRoleConfig = () => {
        if (!user) return null;
        return ROLE_CONFIG[user.role] || null;
    };

    const canEditOwnData = (): boolean => {
        const config = getUserRoleConfig();
        return config?.restrictions?.canEditOwnData || false;
    };

    const canViewAllData = (): boolean => {
        const config = getUserRoleConfig();
        return config?.restrictions?.canViewAllData || false;
    };

    const canDeleteData = (): boolean => {
        const config = getUserRoleConfig();
        return config?.restrictions?.canDeleteData || false;
    };

    const getRoleDisplayName = (): string => {
        const config = getUserRoleConfig();
        return config?.name || user?.roleName || 'Usuario';
    };

    const getRoleDescription = (): string => {
        const config = getUserRoleConfig();
        return config?.description || '';
    };

    return {
        // Funciones básicas
        hasRole,
        hasPermission,
        canAccess,
        canAccessRoute,
        canPerformAction,

        // Verificaciones de rol específico
        isAdmin,
        isSuperAdmin,
        isManager,
        isAgent,

        // Información del usuario
        user,
        userRole: user?.role,
        userPermissions: user?.permissions || [],

        // Configuración del rol
        getUserRoleConfig,
        getRoleDisplayName,
        getRoleDescription,

        // Restricciones
        canEditOwnData,
        canViewAllData,
        canDeleteData,
    };
};